import i18n from '@/helpers/i18n';

// Age brackets in the order the API defines them (keys are the enum names)
export const AGE_BRACKET_KEYS = ['ZeroToFive', 'SixToEleven', 'TwelveToSeventeen', 'Adult', 'Senior'];

export function ageBracketLabel(key) {
  switch (key) {
    case 'ZeroToFive': return '0-5';
    case 'SixToEleven': return '6-11';
    case 'TwelveToSeventeen': return '12-17';
    case 'Adult': return i18n.t('experiences.ageBracketAdult');
    case 'Senior': return '65+';
    default: return key;
  }
}

// Colours given to price periods, in validFrom order (earthy tones that sit well next to the green primary)
export const PERIOD_COLORS = [
  { solid: '#8E3B46', tint: '#F4E3E5' },
  { solid: '#C47F17', tint: '#FBEFD9' },
  { solid: '#2F7F86', tint: '#DDF0F1' },
  { solid: '#6A5ACD', tint: '#E9E6F9' },
  { solid: '#B5562D', tint: '#F7E4DB' },
  { solid: '#3E6EA8', tint: '#E0EAF5' },
  { solid: '#6B7F2A', tint: '#ECF1DC' },
  { solid: '#A0467E', tint: '#F4E2EE' }
];

export function periodColor(index) {
  return PERIOD_COLORS[index % PERIOD_COLORS.length];
}

// 'yyyy-MM-ddT00:00:00' (or a Date) -> 'yyyy-MM-dd', without timezone shifts
export function toIsoDate(value) {
  if (!value) return '';
  if (value instanceof Date) {
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${value.getFullYear()}-${month}-${day}`;
  }
  return String(value).substring(0, 10);
}

// 'yyyy-MM-dd...' -> 'dd/MM/yyyy'
export function formatDateDMY(value) {
  const iso = toIsoDate(value);
  if (!iso) return '';
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
}

export function formatEuro(amount) {
  const value = parseFloat(amount) || 0;
  return '€' + value.toFixed(2);
}

// Compact form for calendar cells: no decimals when the amount is whole
export function formatEuroShort(amount) {
  const value = parseFloat(amount) || 0;
  return '€' + (Number.isInteger(value) ? value : value.toFixed(2));
}

// The one number shown for a price (same rule as the API): PerExperience -> price; PerPerson -> Adult bracket if > 0, else price
export function displayPrice(priceType, price, ageBracketPrices) {
  if (priceType === 'PerPerson' && ageBracketPrices && ageBracketPrices.Adult > 0) {
    return ageBracketPrices.Adult;
  }
  return price || 0;
}

// Brackets with a price, in API order: [{ key, label, price }]. 0 is kept: in a period it means free
export function pricedAgeBrackets(ageBracketPrices) {
  if (!ageBracketPrices) return [];
  return AGE_BRACKET_KEYS
    .filter(key => ageBracketPrices[key] !== null && ageBracketPrices[key] !== undefined && ageBracketPrices[key] !== '' && ageBracketPrices[key] >= 0)
    .map(key => ({ key, label: ageBracketLabel(key), price: parseFloat(ageBracketPrices[key]) }));
}

// A period only carries the brackets it changes; the others keep the base price
export function effectiveAgeBracketPrices(baseAgeBracketPrices, periodAgeBracketPrices) {
  const result = {};
  AGE_BRACKET_KEYS.forEach(key => {
    const base = baseAgeBracketPrices ? parseFloat(baseAgeBracketPrices[key]) : NaN;
    if (base > 0) result[key] = base;
  });
  pricedAgeBrackets(periodAgeBracketPrices).forEach(bracket => { result[bracket.key] = bracket.price; });
  return result;
}

// '€23.00', or 'Gratuito' for 0
export function formatBracketPrice(amount) {
  return parseFloat(amount) === 0 ? i18n.t('pricePeriods.free') : formatEuro(amount);
}
